import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../services/apiClient';
import { useAuth } from '../../../contexts/Auth/authContext';
import { useClientAccess } from '../../../hooks/useClientAccess';
import { useI18n } from '../../../contexts/I18nContext';
import { HardwareType, ModelReference, typesService } from '../../../services/typesService';
import { SortDirection, nextSort } from '../../common/SortableHeader';

/** The columns the backend can order by (`/v2/hardware-types?sort_by=`). */
export type HardwareSortField = 'code' | 'name' | 'device_count' | 'is_active' | 'updated_at';

/**
 * The hardware types list: one server-side page (search, sort, inactive
 * flag), the model references of the types on that page, and the fleet
 * figures from the stats endpoint.
 */
export const useHardwareTypesList = (refreshTrigger?: number) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isSuperAdmin } = useClientAccess();
  const canSeeInactive = isAdmin || isSuperAdmin;
  const { t } = useI18n();

  const [hardwareTypes, setHardwareTypes] = useState<HardwareType[]>([]);
  const [modelRefs, setModelRefs] = useState<Record<number, ModelReference[]>>({});
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [sort, setSort] = useState<{ field: HardwareSortField; direction: SortDirection }>({ field: 'code', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [serverTotalItems, setServerTotalItems] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(0);
  const [stats, setStats] = useState<{ total: number | null; active: number | null; modelRefs: number | null }>({ total: null, active: null, modelRefs: null });

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, showInactive, sort]);

  const includeInactive = showInactive && canSeeInactive;

  const fetchPage = useCallback(async (isMounted: { current: boolean }) => {
    if (!isAuthenticated) {
      if (isMounted.current) setLoading(false);
      return;
    }
    try {
      if (isMounted.current) {
        // First fetch: the spinner. Later ones keep the rows and dim them.
        if (hardwareTypes.length === 0) setLoading(true);
        else setRefetching(true);
        setError(null);
      }
      const query = new URLSearchParams({ page: String(currentPage), limit: String(itemsPerPage), sort_by: sort.field, sort_dir: sort.direction, include_model_refs: 'true' });
      if (includeInactive) query.set('include_inactive', 'true');
      if (debouncedSearch.trim()) query.set('search', debouncedSearch.trim());
      const response = await apiClient.get(`/v2/hardware-types?${query.toString()}`);
      if (!isMounted.current) return;

      // Either {items,total,pages} or a bare array (older deployments).
      const items: HardwareType[] = Array.isArray(response) ? response : (response.items || []);
      setHardwareTypes(items);
      setServerTotalItems(Array.isArray(response) ? items.length : (response.total ?? items.length));
      setServerTotalPages(Array.isArray(response) ? 1 : (response.pages ?? 1));

      // The references come with the page; an older API without them is asked type by type.
      const refs = await Promise.all(items.map((ht) => (Array.isArray(ht.model_refs) ? ht.model_refs : typesService.getModelReferences(ht.id, includeInactive))));
      if (!isMounted.current) return;
      setModelRefs(Object.fromEntries(items.map((ht, i) => [ht.id, refs[i]])));
    } catch (err: unknown) {
      console.error('[HARDWARE TYPES TABLE] Error fetching data:', err);
      if (isMounted.current) setError(err instanceof Error ? err.message : t('deviceTypes.errorLoadingHardware'));
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefetching(false);
      }
    }
  }, [isAuthenticated, includeInactive, currentPage, itemsPerPage, debouncedSearch, sort, hardwareTypes.length, t]);

  useEffect(() => {
    const isMounted = { current: true };
    fetchPage(isMounted);
    return () => { isMounted.current = false; };
  }, [fetchPage, refreshTrigger]);

  const reload = useCallback(() => { fetchPage({ current: true }); }, [fetchPage]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    typesService.getHardwareTypeStats()
      .then((res) => { if (!cancelled) setStats({ total: res.total_types ?? null, active: res.active_types ?? null, modelRefs: res.total_model_refs ?? null }); })
      .catch((e: any) => console.warn('[HARDWARE TYPES TABLE] stats fetch failed:', e?.message || e));
    return () => { cancelled = true; };
  }, [isAuthenticated, refreshTrigger]);

  /** The server already filtered; this only narrows the page while the debounced request is in flight. */
  const rows = useMemo(() => {
    const needle = searchTerm.toLowerCase();
    return hardwareTypes.filter((type) =>
      type.name.toLowerCase().includes(needle) || type.code.toLowerCase().includes(needle) || (type.description?.toLowerCase().includes(needle) ?? false));
  }, [hardwareTypes, searchTerm]);

  /** Fleet-wide figures, with a per-page fallback while the stats request is in flight. */
  const facts = useMemo(() => {
    const total = stats.total ?? hardwareTypes.length;
    const active = stats.active ?? hardwareTypes.filter((ht) => ht.is_active).length;
    const inactive = stats.total != null && stats.active != null ? Math.max(0, stats.total - stats.active) : hardwareTypes.filter((ht) => !ht.is_active).length;
    const modelRefCount = stats.modelRefs ?? Object.values(modelRefs).reduce((sum, refs) => sum + refs.length, 0);
    return { total, active, inactive, modelRefs: modelRefCount };
  }, [hardwareTypes, modelRefs, stats]);

  const maxDevices = useMemo(() => hardwareTypes.reduce((m, ht) => Math.max(m, ht.device_count || 0), 0), [hardwareTypes]);

  return {
    canSeeInactive, loading, refetching, error, rows, modelRefs, facts, maxDevices,
    searchTerm, setSearchTerm, showInactive, setShowInactive,
    sort, sortBy: (field: HardwareSortField) => setSort((s) => nextSort(s, field, ['device_count', 'updated_at'])),
    currentPage, setCurrentPage, itemsPerPage, setItemsPerPage, serverTotalItems, serverTotalPages, reload,
  };
};

export type HardwareTypesListState = ReturnType<typeof useHardwareTypesList>;
